import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  Sliders, 
  Tag, 
  Layers, 
  FileText, 
  Activity,
  PlusCircle,
  History,
  Edit2,
  Trash2,
  Clock,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Calendar,
  Check,
  RotateCcw
} from 'lucide-react';
import { ManipulatedVariable, MVStatus, Subcontroller, StepTest, StepDirection, StepTestStatus } from '../types';

interface EditMvModalProps {
  isOpen: boolean;
  onClose: () => void;
  mv: ManipulatedVariable | null;
  onSave: (updatedMv: ManipulatedVariable, oldTag?: string) => void;
  onDeleteMv?: (mvId: string) => Promise<void> | void;
  existingSubcontrollers?: string[];
  isNew?: boolean;
  steps?: StepTest[];
  onDeleteStep?: (id: string) => Promise<void> | void;
  onUpdateStep?: (step: StepTest) => Promise<void> | void;
  targetMoves?: number;
}

export const EditMvModal: React.FC<EditMvModalProps> = ({
  isOpen,
  onClose,
  mv,
  onSave,
  onDeleteMv,
  existingSubcontrollers = [] as string[],
  isNew = false,
  steps = [] as StepTest[],
  onDeleteStep,
  onUpdateStep,
  targetMoves = 20,
}) => {
  const effectiveTargetMoves = mv?.targetMoves || targetMoves;
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [controller, setController] = useState<Subcontroller>('');
  const [processArea, setProcessArea] = useState('');
  const [units, setUnits] = useState('');
  const [recommendedStepSize, setRecommendedStepSize] = useState<number>(5.0);
  const [approvedStepSize, setApprovedStepSize] = useState<number>(5.0);
  const [status, setStatus] = useState<MVStatus>('Not Started');
  const [notes, setNotes] = useState('');

  // Staged local steps and operations (only committed when Save Changes is clicked)
  const [localSteps, setLocalSteps] = useState<StepTest[]>([]);
  const [stagedDeletedStepIds, setStagedDeletedStepIds] = useState<Set<string>>(new Set());
  const [stagedUpdatedStepIds, setStagedUpdatedStepIds] = useState<Set<string>>(new Set());
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [stepEditForm, setStepEditForm] = useState<StepTest | null>(null);
  const [deletingStepId, setDeletingStepId] = useState<string | null>(null);
  const [confirmDeleteMv, setConfirmDeleteMv] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && mv) {
      setTag(mv.tag || '');
      setDescription(mv.description || '');
      setController(mv.controller || '');
      setProcessArea(mv.processArea || '');
      setUnits(mv.units || '%');
      setRecommendedStepSize(mv.recommendedStepSize || 5.0);
      setApprovedStepSize(mv.approvedStepSize || mv.recommendedStepSize || 5.0);
      setStatus(mv.status || 'Not Started');
      setNotes(mv.notes || '');

      const initialSteps = (steps || []).filter(
        (s) => (mv.id && s.mvId === mv.id) || (mv.tag && s.mvTag?.toUpperCase() === mv.tag.toUpperCase())
      ).sort((a, b) => {
        const timeA = new Date((a.createdAt || a.timestamp || '').replace(' ', 'T')).getTime() || 0;
        const timeB = new Date((b.createdAt || b.timestamp || '').replace(' ', 'T')).getTime() || 0;
        return timeB - timeA;
      });
      setLocalSteps(initialSteps);
      setStagedDeletedStepIds(new Set());
      setStagedUpdatedStepIds(new Set());
      setEditingStepId(null);
      setStepEditForm(null);
      setDeletingStepId(null);
      setConfirmDeleteMv(false);
      setIsSubmitting(false);
    }
  }, [mv?.id, isOpen]);

  if (!isOpen || !mv) return null;

  const isCreating = isNew || !mv.id;
  const isNewSubcontroller = controller.trim() !== '' && !existingSubcontrollers.includes(controller.trim());

  // Derive preview metrics from non-deleted steps in current session
  const activeSteps = localSteps.filter((s) => !stagedDeletedStepIds.has(s.id));
  const passSteps = activeSteps.filter((s) => s.status === 'PASS');
  const previewPositiveMoves = passSteps.filter((s) => s.direction === '+').length;
  const previewNegativeMoves = passSteps.filter((s) => s.direction === '-').length;
  const previewValidMoves = previewPositiveMoves + previewNegativeMoves;
  const previewRejectedMoves = activeSteps.filter((s) => s.status === 'FAIL').length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // 1. Finalize staged deletions in backend
      if (onDeleteStep && stagedDeletedStepIds.size > 0) {
        for (const stepId of Array.from(stagedDeletedStepIds)) {
          await onDeleteStep(stepId);
        }
      }

      // 2. Finalize staged step edits (skip if was deleted)
      if (onUpdateStep && stagedUpdatedStepIds.size > 0) {
        for (const stepId of Array.from(stagedUpdatedStepIds)) {
          if (!stagedDeletedStepIds.has(stepId)) {
            const stepToUpdate = localSteps.find((s) => s.id === stepId);
            if (stepToUpdate) {
              await onUpdateStep(stepToUpdate);
            }
          }
        }
      }

      const finalTag = tag.trim().toUpperCase();
      const oldTag = mv.tag ? mv.tag.trim().toUpperCase() : undefined;

      // 3. Save MV details with reconciled move tallies
      onSave({
        ...mv,
        tag: finalTag,
        description: description.trim(),
        controller: controller.trim() || 'General',
        processArea: processArea.trim(),
        units: units.trim() || '%',
        recommendedStepSize,
        approvedStepSize,
        status,
        validMoves: previewValidMoves,
        positiveMoves: previewPositiveMoves,
        negativeMoves: previewNegativeMoves,
        rejectedMoves: previewRejectedMoves,
        notes: notes.trim(),
        updatedAt: new Date().toISOString(),
      }, oldTag);

      onClose();
    } catch (err) {
      console.error('Error saving changes:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEditStep = (step: StepTest) => {
    setEditingStepId(step.id);
    setStepEditForm({ ...step });
    setDeletingStepId(null);
  };

  const handleCancelEditStep = () => {
    setEditingStepId(null);
    setStepEditForm(null);
  };

  const handleSaveStepEdit = () => {
    if (!stepEditForm) return;
    setLocalSteps((prev) => prev.map((s) => (s.id === stepEditForm.id ? stepEditForm : s)));
    setStagedUpdatedStepIds((prev) => new Set(prev).add(stepEditForm.id));
    setEditingStepId(null);
    setStepEditForm(null);
  };

  const handleStageDeleteStep = (stepId: string) => {
    setStagedDeletedStepIds((prev) => new Set(prev).add(stepId));
    setDeletingStepId(null);
    if (editingStepId === stepId) {
      setEditingStepId(null);
      setStepEditForm(null);
    }
  };

  const handleUndoDeleteStep = (stepId: string) => {
    setStagedDeletedStepIds((prev) => {
      const next = new Set(prev);
      next.delete(stepId);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/50 backdrop-blur-xs overflow-y-auto">
      <div 
        className="bg-white border border-slate-200 rounded-2xl shadow-2xl w-full max-w-2xl sm:max-w-3xl overflow-hidden my-6 animate-in fade-in-50 zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-linear-to-r from-sky-50/60 via-white to-slate-50/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shadow-xs">
              {isCreating ? <PlusCircle className="w-4 h-4 text-sky-600" /> : <Sliders className="w-4 h-4 text-sky-600" />}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-bold text-slate-900 text-base">
                  {isCreating ? 'Add Manipulated Variable' : 'Edit Variable Details'}
                </h2>
                {!isCreating && (
                  <span className="font-mono text-xs font-bold px-2 py-0.5 rounded-md bg-sky-600 text-white shadow-xs">
                    {mv.tag}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {isCreating 
                  ? 'Define variable tag, subcontroller, step perturbation and units'
                  : 'Configure approved step sizes, constraints, and view past moves history'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50"
            title="Close dialog (reverts unsubmitted changes)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4 max-h-[82vh] overflow-y-auto">
          {/* Quick Metrics Bar (only shown when editing existing MV with historical moves) */}
          {!isCreating && (
            <div className="grid grid-cols-3 gap-2.5 p-3 bg-slate-50 rounded-xl border border-slate-200 text-center">
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-400">Valid Moves</div>
                <div className="text-base font-bold font-mono text-emerald-700 mt-0.5">
                  {previewValidMoves} <span className="text-[11px] font-normal text-slate-400">/ {effectiveTargetMoves}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-400">Directional (+/-)</div>
                <div className="text-xs font-bold font-mono text-slate-700 mt-1">
                  <span className="text-emerald-700">+{previewPositiveMoves}</span> / <span className="text-rose-600">-{previewNegativeMoves}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-400">Rejected Moves</div>
                <div className="text-base font-bold font-mono text-rose-600 mt-0.5">
                  {previewRejectedMoves}
                </div>
              </div>
            </div>
          )}

          {/* Variable Tag (Always editable) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-slate-400" />
                <span>Variable Tag / Point Name</span>
              </label>
              {!isCreating && tag.trim().toUpperCase() !== mv.tag.toUpperCase() && (
                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                  Renaming from {mv.tag}
                </span>
              )}
            </div>
            <input
              type="text"
              required
              value={tag}
              onChange={(e) => setTag(e.target.value.toUpperCase())}
              placeholder="e.g. FC-101 or TC-202"
              className="w-full bg-white text-slate-900 font-mono font-bold border border-slate-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
            />
            {!isCreating && tag.trim().toUpperCase() !== mv.tag.toUpperCase() && (
              <span className="text-[10px] text-amber-600 mt-1 block">
                Renaming this tag will also update historical step tests linked to {mv.tag}.
              </span>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-slate-400" />
              <span>Description / Service</span>
            </label>
            <input
              type="text"
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Deethanizer Feed Flow Controller"
              className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
            />
          </div>

          {/* Subcontroller & Process Area */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-slate-400" />
                  <span>Subcontroller</span>
                </label>
                {isNewSubcontroller && (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                    + New Subcontroller
                  </span>
                )}
              </div>
              <input
                type="text"
                list="subcontroller-datalist"
                required
                value={controller}
                onChange={(e) => setController(e.target.value)}
                placeholder="Type or select subcontroller..."
                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
              />
              <datalist id="subcontroller-datalist">
                {existingSubcontrollers.map((sc) => (
                  <option key={sc} value={sc} />
                ))}
              </datalist>
              <span className="text-[10px] text-slate-400 mt-1 block">
                Type any new name to create a subcontroller, or pick from existing
              </span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-slate-400" />
                <span>Process Area</span>
              </label>
              <input
                type="text"
                value={processArea}
                onChange={(e) => setProcessArea(e.target.value)}
                placeholder="e.g. Overhead, Reboiler, Feed"
                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Units & Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Engineering Units
              </label>
              <input
                type="text"
                required
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                placeholder="e.g. m3/h, kPa, °C, %"
                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-slate-400" />
                <span>Variable Status</span>
              </label>
              <select
                value={status === 'Complete' ? 'Step Complete' : status}
                onChange={(e) => setStatus(e.target.value as MVStatus)}
                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-3 py-2 text-xs font-medium focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
              >
                <option value="Not Started">Not Started</option>
                <option value="Active">Active (In Testing)</option>
                <option value="Waiting for Steady State">Waiting for Steady State</option>
                <option value="Step Complete">Step Complete (Steady State)</option>
                <option value="Model Ready">Model Ready (≥{effectiveTargetMoves} Moves)</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Step Sizes (Recommended vs Approved) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 bg-sky-50/40 rounded-xl border border-sky-100">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Recommended Step Size
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={recommendedStepSize}
                onChange={(e) => setRecommendedStepSize(parseFloat(e.target.value) || 0)}
                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
              />
              <span className="text-[10px] text-slate-400 mt-0.5 block">Suggested initial perturbation</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-sky-950 mb-1 flex items-center gap-1">
                <span>Approved Step Size</span>
                <span className="text-[10px] font-bold text-sky-700 bg-sky-100 px-1.5 py-0.2 rounded">Active SP Δ</span>
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                required
                value={approvedStepSize}
                onChange={(e) => setApprovedStepSize(parseFloat(e.target.value) || 0)}
                className="w-full bg-white text-slate-900 border border-sky-400 rounded-lg px-3 py-2 text-xs font-mono font-bold focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none shadow-xs"
              />
              <span className="text-[10px] text-sky-700 mt-0.5 block">Default step magnitude used in tests</span>
            </div>
          </div>

          {/* Engineering Notes & Constraints */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Engineering Notes & Operational Constraints
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Do not move simultaneously with FIC-3010.SP. Check overhead analyzer response within 45 mins."
              className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
            />
          </div>

          {/* Past Steps History Section */}
          {!isCreating && (
            <div className="pt-4 border-t border-slate-200">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center">
                    <History className="w-3.5 h-3.5 text-sky-600" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <span>Past Steps Recorded</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-800 font-mono">
                        {activeSteps.length}
                      </span>
                      {stagedDeletedStepIds.size > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                          {stagedDeletedStepIds.size} pending deletion
                        </span>
                      )}
                    </h3>
                  </div>
                </div>
                <span className="text-[11px] text-slate-400 hidden sm:inline">
                  Deletions and edits are only finalized when clicking Save Changes
                </span>
              </div>

              {localSteps.length === 0 ? (
                <div className="p-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 text-center">
                  <p className="text-xs text-slate-500">No step tests recorded yet for this variable.</p>
                </div>
              ) : (
                <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                  {localSteps.map((step) => {
                    const isStagedForDeletion = stagedDeletedStepIds.has(step.id);
                    const isEditingThisStep = editingStepId === step.id && stepEditForm;
                    const isConfirmingDelete = deletingStepId === step.id;

                    // If marked for deletion, render staged deletion card with Undo option
                    if (isStagedForDeletion) {
                      return (
                        <div
                          key={step.id}
                          className="p-3 rounded-xl bg-rose-50/70 border border-dashed border-rose-300 flex items-center justify-between gap-3 animate-in fade-in-50 duration-150"
                        >
                          <div className="flex items-center gap-2 flex-wrap text-xs text-rose-800">
                            <Trash2 className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                            <span className="font-bold line-through text-slate-400">
                              {step.direction}{step.stepSize} {mv.units}
                            </span>
                            <span className="text-slate-400 text-[11px] font-mono line-through">
                              {step.timestamp || step.createdAt}
                            </span>
                            <span className="text-[11px] font-semibold text-rose-700 bg-rose-100/80 border border-rose-200 px-2 py-0.5 rounded-full">
                              Marked for deletion (reverts if Cancelled)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUndoDeleteStep(step.id);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white border border-rose-200 text-rose-700 hover:bg-rose-100 font-bold transition-colors cursor-pointer shrink-0 shadow-xs"
                            title="Undo deletion of this step move"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            <span>Undo</span>
                          </button>
                        </div>
                      );
                    }

                    // If currently editing this step
                    if (isEditingThisStep && stepEditForm) {
                      return (
                        <div 
                          key={step.id} 
                          className="p-3.5 bg-sky-50/60 rounded-xl border-2 border-sky-400 space-y-3 shadow-xs"
                        >
                          <div className="flex items-center justify-between border-b border-sky-200/60 pb-2">
                            <span className="text-xs font-bold text-sky-900 flex items-center gap-1.5">
                              <Edit2 className="w-3.5 h-3.5 text-sky-600" />
                              <span>Edit Step Record</span>
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono">{step.id}</span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                            {/* Direction & Move Amount */}
                            <div className="sm:col-span-2">
                              <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                                Move Direction & Amount
                              </label>
                              <div className="flex items-center gap-1.5">
                                <div className="flex rounded-lg border border-slate-300 overflow-hidden shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => setStepEditForm({ ...stepEditForm, direction: '+' })}
                                    className={`px-2.5 py-1.5 text-xs font-bold transition-colors cursor-pointer ${
                                      stepEditForm.direction === '+'
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                  >
                                    +
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setStepEditForm({ ...stepEditForm, direction: '-' })}
                                    className={`px-2.5 py-1.5 text-xs font-bold transition-colors cursor-pointer ${
                                      stepEditForm.direction === '-'
                                        ? 'bg-rose-600 text-white'
                                        : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                  >
                                    -
                                  </button>
                                </div>
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  value={stepEditForm.stepSize}
                                  onChange={(e) => setStepEditForm({
                                    ...stepEditForm,
                                    stepSize: parseFloat(e.target.value) || 0
                                  })}
                                  placeholder="Step size"
                                  className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono font-bold focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                                />
                                <span className="text-xs text-slate-500 font-mono shrink-0">{mv.units}</span>
                              </div>
                            </div>

                            {/* Timestamp */}
                            <div className="sm:col-span-2">
                              <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                                Timestamp
                              </label>
                              <input
                                type="text"
                                value={stepEditForm.timestamp}
                                onChange={(e) => setStepEditForm({ ...stepEditForm, timestamp: e.target.value })}
                                placeholder="YYYY-MM-DD HH:mm:ss"
                                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                            {/* Baseline SP */}
                            <div>
                              <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                                Baseline SP
                              </label>
                              <input
                                type="number"
                                step="0.1"
                                value={stepEditForm.baselineSp}
                                onChange={(e) => setStepEditForm({
                                  ...stepEditForm,
                                  baselineSp: parseFloat(e.target.value) || 0
                                })}
                                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                              />
                            </div>

                            {/* Target SP */}
                            <div>
                              <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                                Target SP
                              </label>
                              <input
                                type="number"
                                step="0.1"
                                value={stepEditForm.targetSp}
                                onChange={(e) => setStepEditForm({
                                  ...stepEditForm,
                                  targetSp: parseFloat(e.target.value) || 0
                                })}
                                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                              />
                            </div>

                            {/* Duration (min) */}
                            <div>
                              <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                                Duration (min)
                              </label>
                              <input
                                type="number"
                                step="1"
                                min="1"
                                value={stepEditForm.holdDuration}
                                onChange={(e) => setStepEditForm({
                                  ...stepEditForm,
                                  holdDuration: parseFloat(e.target.value) || 0
                                })}
                                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                              />
                            </div>

                            {/* Status */}
                            <div>
                              <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                                Result Status
                              </label>
                              <select
                                value={stepEditForm.status}
                                onChange={(e) => setStepEditForm({
                                  ...stepEditForm,
                                  status: e.target.value as StepTestStatus
                                })}
                                className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-medium focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                              >
                                <option value="PASS">PASS</option>
                                <option value="FAIL">FAIL</option>
                                <option value="IN_PROGRESS">IN_PROGRESS</option>
                              </select>
                            </div>
                          </div>

                          {/* Comments */}
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                              Comments / Observations
                            </label>
                            <input
                              type="text"
                              value={stepEditForm.comments || ''}
                              onChange={(e) => setStepEditForm({ ...stepEditForm, comments: e.target.value })}
                              placeholder="e.g. Good steady response, analyzer peaked after 25 mins"
                              className="w-full bg-white text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                            />
                          </div>

                          {/* Edit Actions */}
                          <div className="flex items-center justify-end gap-2 pt-1">
                            <button
                              type="button"
                              onClick={handleCancelEditStep}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 transition-colors cursor-pointer"
                            >
                              Cancel Edit
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveStepEdit}
                              className="px-3.5 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                            >
                              <Check className="w-3.5 h-3.5" />
                              <span>Update In Form</span>
                            </button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={step.id}
                        className={`p-3 rounded-xl border transition-all ${
                          isConfirmingDelete 
                            ? 'bg-rose-50/50 border-rose-300' 
                            : 'bg-slate-50 hover:bg-white border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Direction & Move Amount Badge */}
                            {step.direction === '+' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-mono font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-xs">
                                <TrendingUp className="w-3.5 h-3.5" />
                                <span>+{step.stepSize} {mv.units}</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-mono font-bold bg-rose-50 text-rose-700 border border-rose-200 shadow-xs">
                                <TrendingDown className="w-3.5 h-3.5" />
                                <span>-{step.stepSize} {mv.units}</span>
                              </span>
                            )}

                            {/* Status Badge */}
                            {step.status === 'PASS' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                <CheckCircle2 className="w-3 h-3" /> PASS
                              </span>
                            )}
                            {step.status === 'FAIL' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200">
                                <XCircle className="w-3 h-3" /> FAIL
                              </span>
                            )}
                            {step.status === 'IN_PROGRESS' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 animate-pulse">
                                <Clock className="w-3 h-3" /> IN PROGRESS
                              </span>
                            )}

                            {/* Timestamp */}
                            <span className="inline-flex items-center gap-1 text-xs text-slate-500 font-mono">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              <span>{step.timestamp || step.createdAt}</span>
                            </span>
                          </div>

                          {/* Step Edit & Delete Action Buttons */}
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartEditStep(step);
                              }}
                              className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors cursor-pointer"
                              title="Edit this step test"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletingStepId(step.id);
                              }}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                              title="Delete this step test"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Step Details Row */}
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                          <div>
                            <span className="text-slate-400">Setpoint: </span>
                            <span className="font-mono font-bold text-slate-800">{step.baselineSp}</span>
                            <span className="text-slate-400 mx-1">➔</span>
                            <span className="font-mono font-bold text-slate-800">{step.targetSp}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Duration: </span>
                            <span className="font-mono font-semibold text-slate-700">{step.holdDuration} min</span>
                          </div>
                          {(step.engineer || step.operator) && (
                            <div className="text-[11px] text-slate-400">
                              By: {step.engineer || step.operator}
                            </div>
                          )}
                        </div>

                        {/* Comments if any */}
                        {step.comments && (
                          <div className="mt-1.5 text-[11px] text-slate-500 bg-white/80 px-2 py-1 rounded border border-slate-200/60 italic">
                            "{step.comments}"
                          </div>
                        )}

                        {/* Inline Delete Confirmation */}
                        {isConfirmingDelete && (
                          <div className="mt-2.5 p-2.5 rounded-lg bg-rose-50 border border-rose-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 animate-in fade-in-50 duration-150">
                            <div className="flex items-center gap-1.5 text-xs text-rose-800 font-medium">
                              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                              <span>Mark this move for deletion? (Deletion is finalized on Save)</span>
                            </div>
                            <div className="flex items-center gap-2 self-end sm:self-auto">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingStepId(null);
                                }}
                                className="px-2.5 py-1 text-xs rounded-md bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 font-medium transition-colors cursor-pointer"
                              >
                                Keep Step
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStageDeleteStep(step.id);
                                }}
                                className="px-2.5 py-1 text-xs rounded-md bg-rose-600 hover:bg-rose-700 text-white font-bold transition-colors cursor-pointer"
                              >
                                Delete Move
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Modal Footer Actions */}
          <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              {!isCreating && onDeleteMv && (
                confirmDeleteMv ? (
                  <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-lg p-1.5 animate-in fade-in-50">
                    <span className="text-[11px] font-medium text-rose-800">
                      Delete <strong>{mv.tag}</strong> and its steps?
                    </span>
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={async () => {
                        setIsSubmitting(true);
                        try {
                          await onDeleteMv(mv.id);
                          onClose();
                        } catch (err) {
                          console.error('Failed to delete MV:', err);
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                      className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded text-[11px] font-bold shadow-xs cursor-pointer transition-colors"
                    >
                      Confirm Delete
                    </button>
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => setConfirmDeleteMv(false)}
                      className="px-2 py-1 bg-white hover:bg-slate-100 text-slate-700 rounded text-[11px] font-medium border border-slate-200 cursor-pointer transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteMv(true)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer border border-transparent hover:border-rose-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Delete Variable</span>
                  </button>
                )
              )}
              {stagedDeletedStepIds.size > 0 && !confirmDeleteMv && (
                <span className="font-semibold text-rose-600 text-[11px]">
                  {stagedDeletedStepIds.size} move{stagedDeletedStepIds.size > 1 ? 's' : ''} staged for deletion
                </span>
              )}
            </div>

            <div className="flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 hover:shadow cursor-pointer disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                <span>
                  {isSubmitting 
                    ? 'Saving Changes...' 
                    : isCreating 
                      ? 'Create Variable' 
                      : stagedDeletedStepIds.size > 0 
                        ? `Save Changes (${stagedDeletedStepIds.size} deleted)` 
                        : 'Save Changes'}
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};


